import type { ServiceBusHealth } from '../../../types/systemHealth'

interface ThroughputPanelProps {
  data: ServiceBusHealth | undefined
  isLoading: boolean
}

const formatBucketTime = (iso: string): string => {
  const d = new Date(iso)
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })
}

/**
 * SB-vs-legacy throughput.
 *
 * Both figures are `processed` (completed + duplicates), never `completed`
 * alone: the Service Bus consumer cannot tell a duplicate from a fresh insert,
 * so comparing raw completions would make it look faster than it is. During a
 * healthy dual-run the two lines should track each other closely — a persistent
 * gap is the signal this panel exists to surface.
 */
export function ThroughputPanel({ data, isLoading }: ThroughputPanelProps) {
  const throughput = data?.throughput
  const buckets = throughput?.buckets ?? []

  // Shared denominator so the two bar segments are directly comparable.
  const maxBucketCount =
    buckets.reduce(
      (m, b) => Math.max(m, b.serviceBus.processed, b.storageQueue.processed),
      0
    ) || 1

  const sbTotal = throughput?.totals.serviceBus.processed ?? 0
  const legacyTotal = throughput?.totals.storageQueue.processed ?? 0
  const combined = sbTotal + legacyTotal
  const sbShare = combined > 0 ? Math.round((sbTotal / combined) * 100) : 0

  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        Throughput{throughput ? ` (last ${throughput.windowHours}h)` : ''}
      </h2>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !throughput ? (
        <p className="text-sm text-gray-500">No data.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-6 mb-6">
            <Stat label="Service Bus processed" value={sbTotal} />
            <Stat label="Legacy queue processed" value={legacyTotal} />
            <Stat label="Service Bus share" value={`${sbShare}%`} />
          </div>

          <h3 className="text-sm font-medium text-gray-700 mb-2">
            {throughput.bucketMinutes}-minute buckets
          </h3>
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
              <tr>
                <th className="py-2 pr-4">Time</th>
                <th className="py-2 pr-4 text-right">Service Bus</th>
                <th className="py-2 pr-4 text-right">Legacy</th>
                <th className="py-2 pr-4 text-right">Redelivered</th>
                <th className="py-2 w-1/2">Volume</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map((b) => (
                <tr key={b.bucketStart} className="border-b border-gray-100 last:border-0">
                  <td className="py-2 pr-4 font-mono text-gray-700">
                    {formatBucketTime(b.bucketStart)}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums">{b.serviceBus.processed}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{b.storageQueue.processed}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{b.serviceBus.redelivered}</td>
                  <td className="py-2">
                    <div className="space-y-1">
                      <div className="h-3 bg-gray-100 rounded">
                        <div
                          className="h-3 bg-blue-500 rounded"
                          style={{ width: `${(b.serviceBus.processed / maxBucketCount) * 100}%` }}
                        />
                      </div>
                      <div className="h-3 bg-gray-100 rounded">
                        <div
                          className="h-3 bg-gray-400 rounded"
                          style={{ width: `${(b.storageQueue.processed / maxBucketCount) * 100}%` }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
              {buckets.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-3 text-center text-gray-500">
                    {throughput.metricsAvailable
                      ? 'No activity in window.'
                      : 'Counters not enabled.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <p className="text-xs text-gray-400 mt-3">
            <span className="inline-block h-2 w-2 rounded-full bg-blue-500 align-middle mr-1" />
            Service Bus
            <span className="inline-block h-2 w-2 rounded-full bg-gray-400 align-middle ml-4 mr-1" />
            Legacy queue · counts are messages processed (new + duplicate), the only
            figure comparable across both pipelines.
          </p>
        </>
      )}
    </section>
  )
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-2xl font-semibold text-gray-900 tabular-nums">{value}</p>
    </div>
  )
}
